import { Button, GridCellText, StatusBadge, type TableColumn } from '@civilgrid/ui/components';
import { DateTime } from 'luxon';
import type { SubscriptionStatusRow } from './types';

const STATUS_COLOR = { active: 'green', inactive: 'red', exempt: 'grey' } as const;

const formatDate = (value: string | null) => (value ? DateTime.fromISO(value).toLocaleString(DateTime.DATE_MED) : '—');
const formatNumber = (value: number | null) => (value === null ? '—' : value.toLocaleString('en-US'));

export function getSubscriptionColumns(
  onEdit: (row: SubscriptionStatusRow) => void,
): TableColumn<SubscriptionStatusRow>[] {
  return [
    {
      id: 'organizationName',
      header: 'Organization',
      size: 260,
      accessorKey: 'organizationName',
      cell: ({ row }) => <GridCellText>{row.original.organizationName}</GridCellText>,
    },
    {
      id: 'accountType',
      header: 'Account type',
      size: 120,
      accessorKey: 'accountType',
      cell: ({ row }) => <GridCellText>{row.original.accountType}</GridCellText>,
    },
    {
      id: 'opportunity',
      header: 'Opportunity',
      size: 200,
      accessorFn: (row) => row.sfOpportunityName ?? row.sfdcOpportunityId ?? '',
      cell: ({ row }) => (
        <GridCellText>
          {row.original.sfdcOpportunityId
            ? `${row.original.sfOpportunityName ?? row.original.sfdcOpportunityId}${row.original.sfStage ? ` · ${row.original.sfStage}` : ''}`
            : '—'}
        </GridCellText>
      ),
    },
    {
      id: 'contractStartsOn',
      header: 'Contract start',
      size: 120,
      accessorKey: 'contractStartsOn',
      cell: ({ row }) => <GridCellText>{formatDate(row.original.contractStartsOn)}</GridCellText>,
    },
    {
      id: 'contractEndsOn',
      header: 'Contract end',
      size: 120,
      accessorKey: 'contractEndsOn',
      cell: ({ row }) => <GridCellText>{formatDate(row.original.contractEndsOn)}</GridCellText>,
    },
    {
      id: 'sfContractEndsOn',
      header: 'Salesforce end',
      size: 120,
      accessorKey: 'sfContractEndsOn',
      cell: ({ row }) => <GridCellText>{formatDate(row.original.sfContractEndsOn)}</GridCellText>,
    },
    {
      id: 'daysLeft',
      header: 'Days left',
      size: 90,
      accessorKey: 'daysLeft',
      cell: ({ row }) => <GridCellText>{formatNumber(row.original.daysLeft)}</GridCellText>,
    },
    {
      id: 'status',
      header: 'Status',
      size: 100,
      accessorKey: 'status',
      cell: ({ row }) => (
        <StatusBadge
          color={STATUS_COLOR[row.original.status]}
          data-testid={`subscriptions-status-${row.original.organizationId}`}
        >
          {row.original.status}
        </StatusBadge>
      ),
    },
    {
      id: 'sfSyncedAt',
      header: 'Last sync',
      size: 130,
      accessorKey: 'sfSyncedAt',
      cell: ({ row }) => (
        <GridCellText>
          {row.original.sfSyncedAt ? DateTime.fromISO(row.original.sfSyncedAt).toRelative() : 'Never'}
        </GridCellText>
      ),
    },
    {
      id: 'sfContractedUnits',
      header: 'SF contracted',
      size: 110,
      accessorKey: 'sfContractedUnits',
      cell: ({ row }) => <GridCellText>{formatNumber(row.original.sfContractedUnits)}</GridCellText>,
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 90,
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          size="small"
          variant="secondary"
          aria-label={`Edit ${row.original.organizationName}`}
          data-testid={`subscriptions-edit-${row.original.organizationId}`}
          onClick={(event) => {
            event.stopPropagation();
            onEdit(row.original);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];
}
